import React from 'react'
import PropTypes from 'prop-types'
import { useIntl, FormattedMessage } from 'react-intl'
import {
    Icon,
    Box,
    Text,
    VStack,
    HStack,
    Divider,
    Spinner,
    Flex,
    Button,
    Link
} from '@salesforce/retail-react-app/app/components/shared/ui'
import { ChevronRightIcon } from '../icons'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'

/* Context */
import { useStoreLocator } from '../../contexts/store-locator-context';

/* utils */
import { storeModel } from '../../utils/find-in-store-utils'

const FindInStoreList = ({ stores, isFindInStoreLoading, onSelectedStore }) => {
    const intl = useIntl()
    const {app: appConfig} = getConfig()

    const {
        setSelectedStore,
        setStoreCardRef,
        selectedStore,
        setActiveMarkerId
    } = useStoreLocator()

    const handleStoreSelected = (store) => {
        onSelectedStore(store)

        if (store) {
            const selectedStoreMapModel = storeModel([store])[0]
            setSelectedStore(selectedStoreMapModel)
            setActiveMarkerId(selectedStoreMapModel?.id)
        }
    }

    const getAvailabilityInfo = (inventory) => {
        if (inventory > 2) {
            return {
                text: intl.formatMessage(
                    {
                        id: 'pdp_find_in_store.available',
                        defaultMessage: 'Available ({inventory})'
                    },
                    { inventory }
                ),
                color: 'successText'
            }
        } else if (inventory > 0) {
            return {
                text: intl.formatMessage(
                    {
                        id: 'pdp_find_in_store.low',
                        defaultMessage: 'Low Stock ({inventory})'
                    },
                    { inventory }
                ),
                color: 'warningText'
            }
        } else {
            return {
                text: intl.formatMessage({
                    id: 'pdp_find_in_store.oos',
                    defaultMessage: 'Out of Stock'
                }),
                color: 'dangerText'
            }
        }
    }

    // Loading state
    if (isFindInStoreLoading) {
        return (
            <Box textAlign="center" py={24}>
                <Spinner size="lg" color="black" />
                <Text mt={2}>
                    <FormattedMessage
                        id="pdp_find_in_store.loading"
                        defaultMessage="Loading stores..."
                    />
                </Text>
            </Box>
        )
    }

    // No stores found
    if (!stores || stores.length === 0) {
        return (
            <Flex flexDirection="column" gap={4} py={12} px={2}>
                <Text fontSize="lg" fontWeight="bold" textTransform="uppercase">
                    <FormattedMessage
                        id="pdp_find_in_store.no.result"
                        defaultMessage="No result found"
                    />
                </Text>
                <Text fontSize="md" letterSpacing={0}>
                    <FormattedMessage
                        id="pdp_find_in_store.help.description"
                        defaultMessage="We could not find store you are looking for. Please contact our Customer Service."
                    />
                </Text>
                <Button
                    as={Link}
                    href={appConfig?.CUSTOMER_SERVICE_URL}
                    backgroundColor="white"
                    color="black"
                    borderRadius={0}
                    border="1px solid"
                    borderColor="black"
                    mt={2}
                    rel="noopener noreferrer"
                    target='_blank'
                    _hover={{
                        color: 'white',
                        backgroundColor: 'black',
                        textDecoration: 'none'
                    }}
                >
                    <FormattedMessage id="pdp_find_in_store.help" defaultMessage="Help" />
                </Button>
            </Flex>
        )
    }

    // Store list view
    return (
        <VStack align="stretch" spacing={0} py={12}>
            {stores.map((store, index) => {
                const { name, distance, distance_unit, address, inventory } = store
                const { text: availabilityText, color: availabilityColor } =
                    getAvailabilityInfo(inventory)

                return (
                    <Box
                        key={store.store_id}
                        cursor="pointer"
                        onClick={() => handleStoreSelected(store)}
                        onKeyDown={(e) => e.key === 'Enter' && handleStoreSelected(store)?.(e)}
                        ref={setStoreCardRef(store?.store_id, index)}
                        borderColor={selectedStore?.id === store?.store_id ? 'transparent' : 'black'}
                        backgroundColor={selectedStore?.id === store?.store_id && 'warmWhite'}
                        pt={4}
                        pl={2}
                        pr={2}
                        role="button"
                        tabIndex={0}
                        aria-label={name}
                    >
                        <HStack justify="space-between" align="start">
                            <Box>
                                <Text fontWeight="bold" fontSize="md" textTransform="uppercase">
                                    {name}
                                </Text>
                                <Text fontSize="sm" mt={2}>
                                    {address.address1}
                                    <br />
                                    {`${address.city}, ${address.state_code}, ${address.postal_code}`}
                                </Text>
                            </Box>
                            <HStack spacing={1} alignItems="normal">
                                <Text fontSize="xs" whiteSpace="nowrap">
                                    {distance} {distance_unit} away
                                </Text>
                                <Icon as={ChevronRightIcon} />
                            </HStack>
                        </HStack>

                        <HStack mt={4}>
                            <Box w="6px" h="6px" bg={availabilityColor} />
                            <Text fontSize="xs">{availabilityText}</Text>
                        </HStack>

                        {index < stores.length - 1 && <Divider mt={3} borderColor="inherit" />}
                    </Box>
                )
            })}
        </VStack>
    )
}

FindInStoreList.propTypes = {
    stores: PropTypes.array,
    isFindInStoreLoading: PropTypes.bool,
    onSelectedStore: PropTypes.func
}

export default FindInStoreList
